import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Card,
  CardContent,
  Typography,
  Divider,
  TableSortLabel,
  TextField,
  useTheme,
  Grid,
  MenuItem,
  Button,
} from "@mui/material";
import Icons from "../../Utils/Icons";
import { GET } from "../Generic/CrudRequest";
import { searchPermissions } from "../Users/PermissionsUser/PermissionsActions";
import useExcelExport from "../../Utils/useExcelExport";
import { SaveAlt } from "@mui/icons-material";

export default function Clients(props) {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [rows, setRows] = React.useState([]);
  const [orderBy, setOrderBy] = React.useState("");
  const [column, setColumn] = React.useState(0);
  const [order, setOrder] = React.useState("asc");
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [searchText, setSearchText] = useState("");
  const [estatus, setEstatus] = React.useState(true);
  const [register, setRegister] = useState(false);
  const [edit, setEdit] = useState(false);

  const theme = useTheme();
  const { config } = props;
  const exportToExcel = useExcelExport();
  
  const translatedColumns = [
    { id: "codigo", label: t("clients.code"), minWidth: "100px" },
    { id: "nombre", label: t("clients.name"), minWidth: "100px" },
    { id: "rfc", label: t("clients.rfc"), minWidth: "100px" },
    { id: "ciudad", label: t("clients.city"), minWidth: "100px" },
  ];

  const handleExport = () => {
    exportToExcel(rows, translatedColumns, t("clients.excelTitle"));
  };

  useEffect(() => {
    fetchClients();
  }, [page, rowsPerPage, column, order, searchText]);

  const fetchClients = async () => {
    const dataClients = await GET(`${config.url}/api/Client/paginado`, {
      start: page * rowsPerPage,
      length: rowsPerPage,
      nombre: searchText,
      status: estatus,
      column: column,
      dir: order,
    });

    setRows(dataClients.resultado);
    setTotalRecords(dataClients.totalRegistros);

    const permissionsArray = JSON.parse(localStorage.getItem("permissions"));

    const permissionRegister = searchPermissions(
      "Clientes",
      "Registrar",
      permissionsArray
    );

    const permissionEdit = searchPermissions(
      "Clientes",
      "Editar",
      permissionsArray
    );

    setRegister(permissionRegister);
    setEdit(permissionEdit);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRequestSort = (property, columnNumber) => {
    const isAsc = orderBy === property && order === "asc";
    setColumn(columnNumber);
    setOrderBy(property);
    setOrder(isAsc ? "desc" : "asc");
  };

  const handleSearch = () => {
    fetchClients();
  };

  const handleClear = () => {
    setSearchText("");
    setEstatus(true);
  };

  const handleChangeEstatus = (event) => {
    setEstatus(event.target.value);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent>
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "space-between",
            overflow: "hidden",
            maxWidth: "100%",
          }}
        >
          <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
            {t("clients.title")}
          </Typography>
          {register && (
            <Icons
              iconName="Add"
              fontSize="large"
              to="/Clientes/Agregar"
              style={{
                marginLeft: "auto",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.white.main,
              }}
              tooltip={t("buttons.add")}
            />
          )}
        </CardContent>
        <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
        <CardContent
          sx={{
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            alignItems: "auto",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label={t("clients.name")}
                type="search"
                InputLabelProps={{
                  shrink: true,
                }}
                variant="standard"
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  width: 400,
                  maxWidth: "100%",
                  color: theme.palette.primary.main,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("clients.status")}
                InputLabelProps={{
                  shrink: true,
                }}
                name="estatus"
                variant="standard"
                size="small"
                value={estatus}
                fullWidth
                onChange={handleChangeEstatus}
              >
                <MenuItem value={true}>{t("clients.active")}</MenuItem>
                <MenuItem value={false}>{t("clients.inactive")}</MenuItem>
              </TextField>
            </Grid>
            <Icons
              iconName="Search"
              fontSize="large"
              onClick={handleSearch}
              style={{ color: theme.palette.primary.main }}
              tooltip={t("buttons.search")}
            />
            <Icons
              iconName="Clear"
              fontSize="large"
              onClick={handleClear}
              style={{ color: theme.palette.primary.main }}
              tooltip={t("buttons.clear")}
            />
          </Grid>
        </CardContent>
        <Paper
          sx={{
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            overflow: "hidden",
            boxShadow: "none",
            border: "none",
          }}
        >
          <TableContainer>
            <Button
              variant="contained"
              startIcon={<SaveAlt />}
              onClick={handleExport}
            >
              {t("buttons.exportExcel")}
            </Button>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {translatedColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      align="left"
                      style={{ minWidth: column.minWidth, fontWeight: "bold" }}
                    >
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={() =>
                          handleRequestSort(column.id, translatedColumns.indexOf(column))
                        }
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={translatedColumns.length + 1} align="center">
                      {t("clients.noClientsAvailable")}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row, index) => {
                  return (
                    <TableRow key={index}>
                      {translatedColumns.map((column) => (
                        <TableCell key={column.id} align="left">
                          {row[column.id] !== null && row[column.id] !== "" ? (
                            row[column.id]
                          ) : (
                            <i>{t("clients.notAvailable")}</i>
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        {edit && (
                          <Icons
                            iconName="Edit"
                            fontSize="medium"
                            style={{
                              backgroundColor: theme.palette.primary.main,
                              color: "#fff",
                            }}
                            to={`/Clientes/Editar/${row.idCliente}`}
                            tooltip={t("buttons.edit")}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100, { value: -1, label: t("pagination.all") }]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t("pagination.labelRowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              t("pagination.displayedRows", { from, to, count })
            }
          />
        </Paper>
      </CardContent>
    </Card>
  );
}
